import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import curbsideAPIService from '../services/curbsideAPIService';

export const fetchUsers = createAsyncThunk(
  'allUsers/fetchUsers',
  async ({ currentPage, searchTerm, sortConfig, pageSize }: { currentPage: number; searchTerm: string; sortConfig: { key: string; direction: string }; pageSize: number }) => {
    const endpoint = 'staff/all';
    const data = {
      sortBy: sortConfig.key || 'firstname',
      sortDirection: sortConfig.direction || '',
      pageSize: pageSize || 10,
      pageNo: currentPage || 1,
      SearchText: searchTerm || '',
    };

    const response = await curbsideAPIService.post(endpoint, data);
    return response;
  }
);

export const deleteUser = createAsyncThunk('allUsers/deleteUser', async ({ userId }: { userId: string }) => {
  const endpoint = `staff/${userId}`;
  await curbsideAPIService.delete(endpoint, {});
  return userId;
});

export const generatePassword = createAsyncThunk('allUsers/generatePassword', async ({ id }: { id: string }, { rejectWithValue }) => {
  const endpoint = `staff/generatepassword/${id}`;
  try {
    await curbsideAPIService.post(endpoint, {});
    return id;
  } catch (error) {
    console.error('Error generating password:', error);
    return rejectWithValue('Failed to generate password.');
  }
});

const allUsersSlice = createSlice({
  name: 'allUsers',
  initialState: {
    userList: [] as { staffID: string }[],
    totalPages: 0,
    currentPage: 1,
    sortConfig: { key: 'id', direction: 'asc' },
    searchTerm: '',
    isLoading: false,
    pageSize: 10,
    id: null as string | null,
    storeIds: [],
  },
  reducers: {
    setSearchTerm: (state, action) => {
      return {
        ...state,
        searchTerm: action.payload,
        currentPage: 1,
      };
    },
    setSortConfig: (state, action) => {
      return {
        ...state,
        sortConfig: action.payload,
      };
    },
    setCurrentPage: (state, action) => {
      return {
        ...state,
        currentPage: action.payload,
      };
    },
    setStoreIds: (state, action) => {
      state.storeIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        return {
          ...state,
          isLoading: true,
        };
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        return {
          ...state,
          userList: action.payload.items,
          totalPages: Math.ceil(action.payload.totalCount / state.pageSize),
          isLoading: false,
        };
      })
      .addCase(fetchUsers.rejected, (state) => {
        return {
          ...state,
          isLoading: false,
        };
      });

    // Delete user
    builder
      .addCase(deleteUser.pending, (state) => {
        return {
          ...state,
          isLoading: true,
        };
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        const userId = action.payload;
        return {
          ...state,
          userList: state.userList.filter((user) => user.staffID !== userId),
          isLoading: false,
        };
      })
      .addCase(deleteUser.rejected, (state) => {
        return {
          ...state,
          isLoading: false,
        };
      });

    builder
      .addCase(generatePassword.pending, (state) => {
        return {
          ...state,
          isLoading: true,
          error: null,
        };
      })
      .addCase(generatePassword.fulfilled, (state, action) => {
        return {
          ...state,
          id: action.payload,
          isLoading: false,
        };
      })
      .addCase(generatePassword.rejected, (state, action) => {
        return {
          ...state,
          isLoading: false,
          error: action.payload ?? 'An unknown error occurred.',
        };
      });
  },
});

export const { setSearchTerm, setSortConfig, setCurrentPage, setStoreIds } = allUsersSlice.actions;

export default allUsersSlice.reducer;
